// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import {
  IonButton,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonText
} from "@ionic/react"
import { ChangeEvent, useEffect, useState } from "react"
import { useLocales } from "react-localized"
import { TrackableDocument } from "../api"
import { ComponentBackdrop, ContextMenuContainer, useSession } from "../lib"


interface ContainerProps {
  document: TrackableDocument
}


const LoadingPurchaseOrderDetails: React.FC = () => {
  const {gettext} = useLocales()
  return (
    <ContextMenuContainer
      isOpen={true}
      title={gettext("Order")}
    />
  )
}


const PurchaseOrderDetails: React.FC<ContainerProps> = ({
  document
}) => {
  const {gettext} = useLocales()
  const session = useSession()
  const [file, setFile] = useState<File|null>(null)
  const [changed, setChanged] = useState<boolean>(false)
  const [hasInvoice, setHasInvoice] = useState<boolean>(document.hasInvoiceDocument())
  const [invoiceRef, setInvoiceRef] = useState<string|null>(document.getInvoiceRef())
  const [loading, setLoading] = useState<boolean>(false)
  const [trackingCode, setTrackingCode] = useState<string|null>(document.getTrackingCode())

  const handleFileChange: (e: ChangeEvent<HTMLInputElement>) => void = (e) => {
    setFile((e.target.files !== null) ? e.target.files[0] : null)
  }

  const uploadInvoice: (file: File) => Promise<void> = async (file) => {
    await document.uploadInvoice(file)
    setHasInvoice(true)
  }

  useEffect(() => {
    if (file === null) { return }
    session.setLoading(true)
    uploadInvoice(file)
      .then(() => session.setLoading(false))
      .catch(() => session.setError(true))
  }, [file])

  useEffect(() => {
    if (invoiceRef !== document.getInvoiceRef() && !!invoiceRef) {
      document.setInvoiceRef(invoiceRef)
      setChanged(true)
      return
    }
    if (trackingCode !== document.getTrackingCode() && !!trackingCode) {
      document.setTrackingCode(trackingCode)
      setChanged(true)
    }
  }, [invoiceRef, trackingCode])

  return (
  <ContextMenuContainer
    isOpen={true}
    title={gettext("Order")}
  >
    <ComponentBackdrop visible={loading}/>
    <IonList lines="none">
      <IonItem>
        <IonLabel position="stacked">{gettext("Deliver to")}</IonLabel>
        <IonInput readonly>{document.getDeliveryAddress()}</IonInput>
      </IonItem>
      <IonItem lines="full">
        <IonLabel position="stacked">{gettext("Track & Trace")}</IonLabel>
        <IonInput
          onIonInput={(e) => setTrackingCode((e.target.value || "") as string)}
          value={trackingCode}
        />
        <IonNote slot="helper">
          {gettext("The track & trace code assigned to the shipment of the products.")}
        </IonNote>
      </IonItem>
      <IonItem lines="full">
        <IonLabel position="stacked">{gettext("Invoice/Reference No.")}</IonLabel>
        <IonInput
          maxlength={64}
          onIonInput={(e) => setInvoiceRef((e.target.value || "") as string)}
          value={invoiceRef}
        />
        <IonNote slot="helper">
          {gettext("We will include this reference in the financial transaction fulfilling the invoice.")}
        </IonNote>
      </IonItem>
      {(!!hasInvoice) ? null : (<IonItem lines="full">
        <IonLabel position="stacked">{gettext("Upload invoice")}</IonLabel>
        <input type="file" onChange={handleFileChange} />
        <IonNote slot="helper">
          {gettext("Please note that uploading the invoice through this form is the only accepted method to invoice us.")}
        </IonNote>
      </IonItem>)}
      <IonItem>
        <IonLabel position="stacked">{gettext("Email")}</IonLabel>
        <IonText color="primary">
          <a href={`mailto:${document.getContactEmail()}`}>
            {document.getContactName()}
          </a>
        </IonText>
        <br/>
        <IonText color="danger">
          <p>
            <span>{gettext("This email address may be used for inquiries regarding this order.")} </span>
            <span>{gettext("Do not use it for invoicing.")} </span>
            <span>{gettext("Invoice documents sent to this address are not taken into consideration.")}</span>
          </p>
        </IonText>
      </IonItem>
      <IonItem>
        <IonButton
          disabled={!changed}
          style={{width: "100%"}}
          onClick={() => {
            setLoading(true)
            document.update()
              .then(() => {
                setLoading(false)
              })
              .catch(() => session.setError(true))
              .finally(() => setChanged(false))
          }}
        >
          {gettext("Save changes")}
        </IonButton>
      </IonItem>
    </IonList>
  </ContextMenuContainer>
)}

export {LoadingPurchaseOrderDetails, PurchaseOrderDetails};